'use client';
import { CaptchaFormMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import { zodResolver } from '@hookform/resolvers/zod';
import { useId } from 'react';
import { useForm } from 'react-hook-form';
import { type z } from 'zod';
import { boolean as __eliadmin_zod_0 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();

import { signIn } from '@eliadmin/core/src/api/customers/customers-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { signInSchema } from '@eliadmin/core/src/components/auth/sign-in-schema';
import { useAuthNavigation } from '@eliadmin/core/src/components/dlz/auth/dialog/auth-navigation';
import { SIGN_IN_COPY } from '@eliadmin/core/src/components/dlz/auth/sign-in/sign-in-copy';
import { setSessionAuthToken } from '@eliadmin/core/src/components/dlz/auth/sign-in/session-token';
import { FormRow } from '@eliadmin/core/src/components/dlz/forms/form-row';
import { PasswordInput } from '@eliadmin/core/src/components/dlz/forms/password-input';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Form, FormField } from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import {
  useFormValidationMessages,
  type FormValidationMessages,
} from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { setAuthToken } from '@eliadmin/core/src/server/api-client';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export { signInSchema } from '@eliadmin/core/src/components/auth/sign-in-schema';

type SignInFormProps = {
  /** Inside the dialog the card has no register promo next to it, so the form links to it itself. */
  inDialog?: boolean;
};

const signInFormSchema = (messages: FormValidationMessages) =>
  signInSchema(messages).extend({ remember: __eliadmin_zod_0() });

type SignInFormInput = z.input<ReturnType<typeof signInFormSchema>>;
type SignInFormOutput = z.output<ReturnType<typeof signInFormSchema>>;

const FIELD_NAMES = ['email', 'password'] as const;

const isFieldName = (name: string): name is (typeof FIELD_NAMES)[number] =>
  FIELD_NAMES.some(field => field === name);

const focusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012] focus-visible:ring-offset-2';

/**
 * Login form of the DLZ sign-in page and dialog (Figma „Login card“ 60155:18079 without its heading):
 * e-mail, password, „Zapamätať si ma“ + „Zabudli ste heslo?“, the button and the encryption note.
 * Core behaviour is unchanged: reCAPTCHA, 422 → field errors, destructive toast, auth cookie,
 * customer store, redirect to the profile. The checkbox decides whether the cookie lasts a year
 * (core `setAuthToken`) or only for the browser session. Always rendered inside the authentication
 * dialog, which decides where to go after the sign-in and shows the other views.
 */
export function SignInForm({ inDialog }: SignInFormProps) {
  const { t } = useTranslations();
  const { formValidationMessages } = useFormValidationMessages(t);
  const { toast } = useToast();
  const { showPasswordReset, showRegister, signedIn } = useAuthNavigation();
  const locale = useLocaleStore(state => state.currentLocale);

  const rememberId = useId();

  const form = useForm<SignInFormInput, undefined, SignInFormOutput>({
    resolver: zodResolver(signInFormSchema(formValidationMessages)),
    defaultValues: { email: '', password: '', 'g-recaptcha-response': '', remember: true },
  });
  const serverError = form.formState.errors.root?.server?.message;

  const handleSubmit = form.handleSubmit(async ({ remember, ...credentials }) => {
    try {
      const captcha = '';
      const { data } = await signIn({ ...credentials, locale });

      if (remember) await setAuthToken({ authToken: data.token });
      else await setSessionAuthToken(data.token);

      initCustomerStore({ authToken: data.token, customer: data.customer });
      toast({ title: t('text656'), variant: 'success' });
      signedIn();
    } catch (error) {
      if (error instanceof ValidationError) {
        for (const [field, messages] of Object.entries(error.fields)) {
          form.setError(isFieldName(field) ? field : 'root.server', {
            type: 'server',
            message: messages.join('\n'),
          });
        }
        return;
      }

      toast({
        title: t('text594'),
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      });
    }
  });

  return (
    <Form {...form}>
      <CaptchaFormMessage errors={form.formState.errors} />
      <form noValidate onSubmit={handleSubmit} className="flex flex-col gap-6">
        <div className="flex flex-col gap-5">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormRow label={t('text35')} required>
                <Input
                  {...field}
                  type="email"
                  inputMode="email"
                  autoComplete="username"
                  placeholder={SIGN_IN_COPY.emailPlaceholder}
                />
              </FormRow>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormRow label={t('text191')} required>
                <PasswordInput
                  {...field}
                  autoComplete="current-password"
                  placeholder={SIGN_IN_COPY.passwordPlaceholder}
                />
              </FormRow>
            )}
          />
          <div className="flex flex-col items-end gap-3 sm:flex-row sm:items-center sm:justify-between">
            <FormField
              control={form.control}
              name="remember"
              render={({ field }) => (
                <div className="flex items-center gap-3 self-start sm:self-auto">
                  <Checkbox
                    id={rememberId}
                    name={field.name}
                    ref={field.ref}
                    checked={field.value}
                    onBlur={field.onBlur}
                    onCheckedChange={checked => field.onChange(checked === true)}
                  />
                  <label htmlFor={rememberId} className="dlz-body-m text-[#0F1012]">
                    {SIGN_IN_COPY.remember}
                  </label>
                </div>
              )}
            />
            <button
              type="button"
              onClick={showPasswordReset}
              className={cn(
                'dlz-body-s rounded-sm text-[#6B7079] underline transition-colors hover:text-[#0F1012]',
                focusRing,
              )}
            >
              {SIGN_IN_COPY.forgotPassword}
            </button>
          </div>
        </div>

        <div className="flex flex-col items-center gap-4">
          {serverError ? (
            <p role="alert" className="dlz-caption w-full whitespace-pre-line text-[#D7191F]">
              {serverError}
            </p>
          ) : null}
          <Button
            type="submit"
            isLoading={form.formState.isSubmitting}
            className="h-12 w-full md:h-[52px]"
          >
            <span className="dlz-trim">{t('text189')}</span>
          </Button>
          <p className="dlz-caption flex items-center justify-center gap-1 text-[#6B7079]">
            <Icon
              name="lock-key"
              size={20}
              // The Figma instance keeps the 1.5 px stroke at 20 px: 1.5 / (20 / 24).
              className="text-[#9096A0] [&_path]:[stroke-width:1.8]"
            />
            {SIGN_IN_COPY.secureNote}
          </p>
          {inDialog ? (
            <p className="dlz-body-s text-center text-[#4F545C]">
              {t('text193')}{' '}
              <button
                type="button"
                onClick={showRegister}
                className={cn(
                  'dlz-body-s-medium rounded-sm text-[#D7191F] hover:text-[#B5141A] hover:underline',
                  focusRing,
                )}
              >
                {t('text758')}
              </button>
            </p>
          ) : null}
        </div>
      </form>
    </Form>
  );
}
